import { Trash } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { ComponentStatus } from 'bom/enums';
import { Button } from 'core/components';
import { useAgGridTableContext } from 'core/states';
import { IJobPart } from 'jobs/types';
import { Color, Variant } from 'theme/enums';

import { RemoveConfirmation } from './RemoveConfirmation';

interface IProps {
  jobPart?: IJobPart;
  buttonElement?: ReactElement;
}

export const RemoveButton = ({ jobPart, buttonElement }: IProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const queryClient = useQueryClient();

  const { t } = useTranslation();

  const { selectedRows, deselectAllRows } = useAgGridTableContext();

  const rowsToRemove = useMemo(
    () => (selectedRows.length > 0 ? selectedRows : [jobPart]),
    [jobPart, selectedRows]
  );

  const isDisabled = useMemo(
    () =>
      rowsToRemove.length === 0 ||
      rowsToRemove.every((row) => row.status === ComponentStatus.DELETED),
    [rowsToRemove]
  );

  const onSubmitSuccess = () => {
    deselectAllRows();
    queryClient.invalidateQueries(['jobParts']);
  };

  const button = useMemo(() => {
    if (buttonElement) {
      return cloneElement(buttonElement, { onClick: () => setIsOpen(true), disabled: isDisabled });
    }

    return (
      <Button
        text={t('buttonLabelRemove')}
        variant={isDisabled ? Variant.OUTLINED : Variant.SOFT}
        color={Color.DANGER}
        startDecorator={<Trash />}
        onClick={() => setIsOpen(true)}
        disabled={isDisabled}
      />
    );
  }, [buttonElement, isDisabled, t]);

  return (
    <>
      {button}

      <RemoveConfirmation
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        selectedRows={rowsToRemove}
        onSubmitSuccess={onSubmitSuccess}
      />
    </>
  );
};
